import React from "react";

const RadarChart = ({ scoreAllList, userName, createdAt }) => {
  // Threshold legends mapping
  const legends = [
    { range: '10-24', text: 'Low', dotColor: 'bg-[#E53935]' },
    { range: '25- 37', text: 'Moderate', dotColor: 'bg-[#FFA000]' },
    { range: '38-50', text: 'High', dotColor: 'bg-[#43A047]' },
  ];

  // Radar Chart Data
  const center = 200;
  const maxRadius = 150;
  const maxScore = 50;

  const PhysicalScore =
    scoreAllList.find((item) => item.name === "Physical")?.score || 0;

  const EmotionalScore =
    scoreAllList.find((item) => item.name === "Emotional")?.score || 0;

  const MentalScore =
    scoreAllList.find((item) => item.name === "Mental")?.score || 0;

  const SpiritualScore =
    scoreAllList.find((item) => item.name === "Spiritual")?.score || 0;

  const getRadius = (score) => (score / maxScore) * maxRadius;

  const polygonPoints = [
    `${center},${center - getRadius(PhysicalScore)}`, // Top
    `${center + getRadius(EmotionalScore)},${center}`, // Right
    `${center},${center + getRadius(MentalScore)}`, // Bottom
    `${center - getRadius(SpiritualScore)},${center}`, // Left
  ].join(" ");

  const pointCoords = [
    {
      x: center,
      y: center - getRadius(PhysicalScore),
      color: "fill-sky-700",
    },
    {
      x: center + getRadius(EmotionalScore),
      y: center,
      color: "fill-purple-700",
    },
    {
      x: center,
      y: center + getRadius(MentalScore),
      color: "fill-teal-600",
    },
    {
      x: center - getRadius(SpiritualScore),
      y: center,
      color: "fill-rose-700",
    },
  ];

  return (
    <div className="p-10 px-6 space-y-6 bg-white">
      <div className="px-12 space-y-6 page-break">
        {/* Header */}
        <div className="flex items-center justify-between w-full pt-4 pb-2 border-b text-[#212121]/85 italic">
          <h1 className="text-lg font-semibold">Energy Profile Report</h1>
          <p className="text-lg font-semibold">{userName} | {createdAt}</p>
        </div>

        {/* Title */}
        <div className="mt-6 mb-4">
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
            Energy Profile (Capacity 0-50 per dimension)
          </h1>
          <p className="text-base text-gray-600 mt-1">
            A snapshot of your scores across the four flow profiles.
          </p>
        </div>

        {/* Radar Chart */}
        <div className="relative w-full max-w-lg mx-auto aspect-square my-8 flex items-center justify-center">
          {/* Top */}
          <div className="absolute top-0 text-center">
            <span className="block font-bold text-sky-700 text-lg">Physical</span>
            <span className="block font-bold text-sky-700 text-lg">{"( " + PhysicalScore + " )"}</span>
          </div>

          {/* Right */}
          <div className="absolute -right-24 text-right">
            <span className="block font-bold text-purple-700 text-lg">Emotional</span>
            <span className="block font-bold text-purple-700 text-lg">{"( " + EmotionalScore + " )"}</span>
          </div>

          {/* Bottom */}
          <div className="absolute bottom-0 text-center">
            <span className="block font-bold text-teal-700 text-lg">Mental</span>
            <span className="block font-bold text-teal-700 text-lg">{"( " + MentalScore + " )"}</span>
          </div>

          {/* Left */}
          <div className="absolute -left-24 text-left">
            <span className="block font-bold text-rose-700 text-lg">Spiritual</span>
            <span className="block font-bold text-rose-700 text-lg">{"( " + SpiritualScore + " )"}</span>
          </div>

          <svg
            viewBox="0 0 400 400"
            className="w-[90%] h-[90%] overflow-visible"
          >
            {/* Grid Rings */}
            {[10, 20, 30, 40, 50].map((val) => {
              const radius = (val / maxScore) * maxRadius;

              return (
                <g key={val}>
                  <polygon
                    points={`
                      ${center},${center - radius}
                      ${center + radius},${center}
                      ${center},${center + radius}
                      ${center - radius},${center}
                    `}
                    fill="none"
                    stroke="#e2e8f0"
                    strokeWidth="1"
                  />

                  <text
                    x={center}
                    y={center - radius + 4}
                    className="text-[14px] fill-gray-700 font-medium"
                    textAnchor="middle"
                  >
                    {val}
                  </text>
                </g>
              );
            })}

            {/* Center Label */}
            <text
              x={center}
              y={center + 4}
              className="text-[12px] fill-gray-700 font-medium"
              textAnchor="middle"
            >
              0
            </text>

            {/* Axis Lines */}
            <line
              x1={center}
              y1={center - maxRadius}
              x2={center}
              y2={center + maxRadius}
              stroke="#e2e8f0"
              strokeWidth="1"
            />

            <line
              x1={center - maxRadius}
              y1={center}
              x2={center + maxRadius}
              y2={center}
              stroke="#e2e8f0"
              strokeWidth="1"
            />

            {/* Radar Area */}
            <polygon
              points={polygonPoints}
              fill="rgba(20,184,166,0.08)"
              stroke="#14b8a6"
              strokeWidth="3"
            />

            {/* Data Points */}
            {pointCoords.map((point, index) => (
              <circle
                key={index}
                cx={point.x}
                cy={point.y}
                r="2"
                className={point.color}
              />
            ))}
          </svg>
        </div>

        {/* Legends */}
        <div className="border border-gray-300 rounded-xl bg-gray-50/50 px-12 py-4 grid grid-cols-2 sm:flex sm:flex-wrap items-center justify-between gap-4 mb-6">
          {legends.map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-base mx-auto sm:mx-0 text-gray-900">
              <span className={`w-2.5 h-2.5 rounded-full ${item.dotColor}`} />
              <span className="block font-medium">{item.range}</span>
              <span className="font-medium">{item.text}</span>
            </div>
          ))}
        </div>

        {/* Score Cards */}
        <div className="border border-gray-300 rounded-xl p-4 grid grid-cols-2 md:grid-cols-4 gap-y-6 divide-x-0 sm:divide-x divide-gray-300">
          {scoreAllList.map((card, idx) => (
            <div key={idx} className="flex flex-col items-center justify-center text-center px-2 space-y-2">
              <span className={`text-lg font-bold ${card.scoreColor}`}>{card.name}</span>
              <div className="text-lg font-bold text-gray-800">
                <span className={card.scoreColor}>{card.score + " / "}</span>
                <span className="text-gray-400 font-bold text-sm">{" " + card.maxScore}</span>
              </div>
              <span className={`text-base font-bold px-2.5 py-1 w-32 text-center rounded border tracking-wider ${card.colorClass}`}>{card.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RadarChart;